import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authIntegration } from '@/lib/api/client';
import { decideSsoCallback, exchangeSsoCodeOnce } from '@/lib/auth/sso-code-exchange.js';
import { useAuthStore } from '@/store/auth.store.js';
import { useTenantStore } from '@/store/tenant.store';

function readCallbackCode() {
    return new URLSearchParams(window.location.search).get('code');
}

function sessionIsStored() {
    return Boolean(useAuthStore.getState().token);
}

function rememberTenant(user, fallbackTenantId) {
    const tenantId = user?.memberships?.[0]?.tenant?.id
        || user?.memberships?.[0]?.tenant?._id
        || fallbackTenantId;
    if (tenantId) useTenantStore.getState().setSelectedTenantId?.(tenantId);
}

export default function SsoCallbackPage() {
    const { t } = useTranslation('login');
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const tRef = useRef(t);
    tRef.current = t;

    useEffect(() => {
        let active = true;
        const code = readCallbackCode();
        const decision = decideSsoCallback({ code, hasSession: sessionIsStored() });

        if (decision === 'home') {
            navigate('/home', { replace: true });
            return undefined;
        }
        if (decision === 'login') {
            navigate('/login?account=inactive', { replace: true });
            return undefined;
        }

        // `t` is intentionally not a dependency. The login namespace loads after
        // first paint and would restart this effect, posting the one-time code twice.
        exchangeSsoCodeOnce(code, (value) => authIntegration.ssoExchange(value)).then((result) => {
            if (result?.success || sessionIsStored()) {
                rememberTenant(result?.data?.user || useAuthStore.getState().user, result?.data?.tenant?.id);
                navigate('/home', { replace: true });
                return;
            }
            if (!active) return;
            setMessage(result?.error || tRef.current('errors.ssoFailed', { defaultValue: 'Sign-in from the portal failed.' }));
        }).catch(() => {
            if (sessionIsStored()) {
                navigate('/home', { replace: true });
                return;
            }
            if (!active) return;
            setMessage(tRef.current('errors.ssoFailed', { defaultValue: 'Sign-in from the portal failed.' }));
        });

        return () => {
            active = false;
        };
    }, [navigate]);

    useEffect(() => {
        const onPageShow = (event) => {
            if (!event.persisted || !sessionIsStored()) return;
            navigate('/home', { replace: true });
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [navigate]);

    return (
        <p className="p-8 text-sm text-muted-foreground">
            {message || t('sso.working', { defaultValue: 'Signing you in…' })}
        </p>
    );
}
